/**
 * attendanceHelpers.js — Puantaj yardımcı fonksiyonları
 * Formatlayıcılar, durum eşlemeleri, renk sabitleri
 */

export const ATTENDANCE_STATUS = {
  PRESENT: 'PRESENT',
  LATE: 'LATE',
  ABSENT: 'ABSENT',
  SICK_REPORT: 'SICK_REPORT',
  EARLY_OUT: 'EARLY_OUT',
  LATE_EARLY_OUT: 'LATE_EARLY_OUT',
  MISSING_CLOCK_IN: 'MISSING_CLOCK_IN',
  MISSING_CLOCK_OUT: 'MISSING_CLOCK_OUT',
};

export const TIMESHEET_STATUS = {
  DRAFT: 'DRAFT',
  CALCULATED: 'CALCULATED',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  LOCKED: 'LOCKED',
  EXPORTED: 'EXPORTED',
};

export const APPROVAL_STATUS = {
  PENDING: 'PENDING',
  MANAGER_APPROVED: 'MANAGER_APPROVED',
  HR_APPROVED: 'HR_APPROVED',
  REJECTED: 'REJECTED',
};

/** Durum → Tailwind renk sınıfı (badge) */
export const getStatusBadgeClass = (status) => {
  const map = {
    PRESENT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    LATE: 'bg-amber-50 text-amber-700 border-amber-200',
    ABSENT: 'bg-rose-50 text-rose-700 border-rose-200',
    SICK_REPORT: 'bg-sky-50 text-sky-700 border-sky-200',
    EARLY_OUT: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
    LATE_EARLY_OUT: 'bg-orange-50 text-orange-700 border-orange-200',
    MISSING_CLOCK_IN: 'bg-red-50 text-red-600 border-red-200',
    MISSING_CLOCK_OUT: 'bg-red-50 text-red-600 border-red-200',
  };
  return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
};

/** Durum → Takvim hücre rengi (dot + bg) */
export const getStatusCalendarStyle = (status) => {
  const map = {
    PRESENT: { dot: 'bg-emerald-500', bg: 'bg-emerald-50', ring: 'ring-emerald-200' },
    LATE: { dot: 'bg-amber-500', bg: 'bg-amber-50', ring: 'ring-amber-200' },
    ABSENT: { dot: 'bg-rose-500', bg: 'bg-rose-50', ring: 'ring-rose-200' },
    SICK_REPORT: { dot: 'bg-sky-500', bg: 'bg-sky-50', ring: 'ring-sky-200' },
    HOLIDAY: { dot: 'bg-violet-400', bg: 'bg-violet-50', ring: 'ring-violet-200' },
    WEEKEND: { dot: 'bg-slate-300', bg: 'bg-slate-50', ring: 'ring-slate-100' },
  };
  return map[status] || { dot: 'bg-slate-300', bg: 'bg-slate-50', ring: 'ring-slate-100' };
};

/** Onay durumu → badge rengi */
export const getApprovalBadgeClass = (approval) => {
  const map = {
    PENDING: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    MANAGER_APPROVED: 'bg-blue-50 text-blue-700 border-blue-200',
    HR_APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    REJECTED: 'bg-rose-50 text-rose-700 border-rose-200',
  };
  return map[approval] || 'bg-slate-100 text-slate-600 border-slate-200';
};

/** Timesheet durumu → badge rengi */
export const getTimesheetStatusBadgeClass = (status) => {
  const map = {
    DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
    CALCULATED: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    PENDING_APPROVAL: 'bg-amber-50 text-amber-700 border-amber-200',
    APPROVED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    LOCKED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    EXPORTED: 'bg-violet-50 text-violet-700 border-violet-200',
  };
  return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
};

/** Saat formatı: "08:45" → 8.75 (ondalık saat) */
export const timeToDecimal = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h + m / 60;
};

/** Ondalık saat → "8h 45m" */
export const decimalToDisplay = (hours) => {
  if (hours == null || isNaN(hours)) return '-';
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0 && m === 0) return '0h';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

/** Tarih → ISO string (YYYY-MM-DD) */
export const toDateISO = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return d.toISOString().split('T')[0];
};

/** Bugünün ISO tarihi */
export const todayISO = () => new Date().toISOString().split('T')[0];

/** Ay başı ISO tarihi */
export const monthStartISO = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

/** Gün sayısı farkı */
export const daysBetween = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  return Math.max(1, Math.ceil((e - s) / (1000 * 60 * 60 * 24)) + 1);
};

/** Departman listesinden ID→isim map'i oluştur */
export const buildDepartmentMap = (departments) => {
  if (!departments?.length) return {};
  return departments.reduce((acc, d) => {
    acc[d.id || d.department_id] = d.name || d.department_name || '';
    return acc;
  }, {});
};

/** Kayıtlardan özet istatistik hesapla (KPI kartları için) */
export const computeStats = (records) => {
  let present = 0, late = 0, absent = 0, sick = 0, totalHours = 0, pendingApproval = 0;
  let todayPresent = 0, todayLate = 0, todayAbsent = 0;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  (records || []).forEach((rec) => {
    const recordDate = rec.date?.split('T')[0] || rec.date;
    totalHours += rec.total_work_hours || 0;
    if (rec.status === 'PRESENT') present++;
    else if (rec.status === 'LATE' || rec.status === 'EARLY_OUT' || rec.status === 'LATE_EARLY_OUT') late++;
    else if (rec.status === 'ABSENT' || rec.status === 'MISSING_CLOCK_IN' || rec.status === 'MISSING_CLOCK_OUT') absent++;
    if (rec.record_type === 'SICK_REPORT') sick++;
    if (rec.approval_status === 'PENDING' || rec.approval === 'PENDING') pendingApproval++;

    if (recordDate === today) {
      if (rec.status === 'PRESENT') todayPresent++;
      else if (rec.status === 'LATE' || rec.status === 'EARLY_OUT' || rec.status === 'LATE_EARLY_OUT') todayLate++;
      else if (rec.status === 'ABSENT' || rec.status === 'MISSING_CLOCK_IN' || rec.status === 'MISSING_CLOCK_OUT') todayAbsent++;
    }
  });

  return {
    present,
    late,
    absent,
    sick,
    totalHours: totalHours.toFixed(1),
    pendingApproval,
    total: records?.length || 0,
    todayPresent,
    todayLate,
    todayAbsent,
  };
};